/**
 * @file MultiSelectSearchInputView — presentational type-to-filter input row
 * for the MultiSelect dropdown. Renders an Input atom with a leading search
 * icon and an optional trailing clear affordance.
 */
import { Search, X } from "lucide-react-native";
import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Input } from "@/components/atoms/Input";

import { TEST_ID_SIMILAR_PLAYER } from "e2e/testId";

/**
 * Props for MultiSelectSearchInputView.
 */
interface MultiSelectSearchInputViewProps {
  /** Current search text (controlled by the container). */
  readonly value: string;
  /** Placeholder for the search input. */
  readonly placeholder: string;
  /** Whether the clear affordance should be shown. */
  readonly showClear: boolean;
  /** Handler invoked as the user types. */
  readonly onChangeText: (value: string) => void;
  /** Handler invoked when the clear affordance is pressed. */
  readonly onClear: () => void;
}

/**
 * Renders the search input row above the option list.
 * @param root0 - Component props
 * @param root0.value - Current search text
 * @param root0.placeholder - Placeholder for the search input
 * @param root0.showClear - Whether the clear affordance should be shown
 * @param root0.onChangeText - Handler invoked as the user types
 * @param root0.onClear - Handler invoked when the clear affordance is pressed
 * @returns The search input row
 */
const MultiSelectSearchInputView = ({
  value,
  placeholder,
  showClear,
  onChangeText,
  onClear,
}: MultiSelectSearchInputViewProps) => (
  <Box className="p-2">
    <Input
      variant="outline"
      size="md"
      leadingIcon={Search}
      trailingIcon={showClear ? X : undefined}
      onTrailingIconPress={onClear}
      field={{
        testID: TEST_ID_SIMILAR_PLAYER.MULTISELECT_SEARCH_INPUT,
        value,
        onChangeText,
        placeholder,
        autoCapitalize: "none",
        autoCorrect: false,
      }}
    />
  </Box>
);

MultiSelectSearchInputView.displayName = "MultiSelectSearchInputView";

export default memo(MultiSelectSearchInputView);
